import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Search, MapPin, Music2, Compass, Loader2, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${u.startsWith("/") ? u : "/" + u}`;
}

export default function DiscoverPage() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [country, setCountry] = useState("");
  const [results, setResults] = useState([]);
  const [genres, setGenres] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [g, c] = await Promise.all([api.get("/tracks/genres"), api.get("/tracks/countries")]);
        setGenres(g.data);
        setCountries(c.data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const tm = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/discover/artists", { params: { q: q.trim(), genre, country, limit: 36 } });
        setResults(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(tm);
  }, [q, genre, country]);

  const toggleFollow = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/follow`);
      setResults((arr) => arr.map((a) => a.id === id ? { ...a, is_following: data.following, followers: data.followers } : a));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="discover-page">
      <header className="flex items-center gap-3">
        <Compass className="w-7 h-7 text-[#FFD60A]" />
        <h1 className="text-3xl sm:text-4xl font-black font-display text-white">{t.discover?.title ?? "Découverte"}</h1>
      </header>

      <div className="grid sm:grid-cols-[1fr_180px_180px] gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.discover?.searchPh ?? "Rechercher un artiste…"} className="input-base pl-9" data-testid="discover-search" />
        </div>
        <select value={genre} onChange={(e) => setGenre(e.target.value)} className="input-base" data-testid="discover-genre">
          <option value="">{t.discover?.allGenres ?? "Tous genres"}</option>
          {genres.map((g) => <option key={g.genre} value={g.genre}>{g.genre} ({g.count})</option>)}
        </select>
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="input-base" data-testid="discover-country">
          <option value="">{t.discover?.allCountries ?? "Tous pays"}</option>
          {countries.map((c) => <option key={c.country} value={c.country}>{c.country} ({c.count})</option>)}
        </select>
      </div>

      {loading && results.length === 0 ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 text-zinc-500 animate-spin" /></div>
      ) : results.length === 0 ? (
        <p className="text-zinc-500 text-center py-16">{t.discover?.empty ?? "Aucun artiste trouvé."}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="discover-grid">
          {results.map((a) => (
            <div key={a.id} className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition-all group" data-testid={`discover-card-${a.id}`}>
              <Link to={`/profile/${a.id}`} className="block aspect-[4/5] relative">
                {a.cover ? (
                  <img src={resolveImg(a.cover)} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="flex items-end gap-2 mb-1">
                    {a.avatar ? (
                      <img src={resolveImg(a.avatar)} alt={a.name} className="w-11 h-11 rounded-full border-2 border-white object-cover" />
                    ) : (
                      <div className="w-11 h-11 rounded-full grid place-items-center bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] text-white font-black border-2 border-white">{a.name?.[0]?.toUpperCase()}</div>
                    )}
                  </div>
                  <div className="text-white text-sm font-black truncate">{a.name}</div>
                  <div className="flex items-center gap-1.5 text-[10px] text-zinc-300 flex-wrap">
                    {a.country && (<span className="inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{a.country}</span>)}
                    {a.country && a.genre && <span className="text-zinc-500">·</span>}
                    {a.genre && (<span className="inline-flex items-center gap-0.5"><Music2 className="w-2.5 h-2.5" />{a.genre}</span>)}
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-1">{Intl.NumberFormat().format(a.followers || 0)} abonnés</div>
                </div>
              </Link>
              <button
                onClick={() => toggleFollow(a.id)}
                className={`w-full py-2 text-xs font-black ${a.is_following ? "bg-white/5 text-zinc-300 hover:bg-white/10" : "bg-white text-black hover:opacity-90"}`}
                data-testid={`discover-follow-${a.id}`}
              >
                {a.is_following ? <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> {t.profile?.following ?? "Suivi(e)"}</span>
                                : <span className="inline-flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> {t.profile?.follow ?? "Suivre"}</span>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
